<template>
  <section class="overview-dashboard">
    <!-- TOP STRIP -->
    <section class="filter-card">
      <div class="filter-header">
        <div class="section-title">
          <span class="section-icon">
            <q-icon :size="$q.screen.lt.md ? 'md' : ''" name="dashboard" />
          </span>
          <div class="ower-view">
            <span class="text-h6">Overview</span>
            <p class="header-copy">
              Overview of dealer and market dashboard activity.
            </p>
          </div>
        </div>

        <div class="header-actions-inline">
          <q-badge
            v-if="usingDummyData"
            color="orange-2"
            text-color="orange-9"
            class="demo-badge"
          >
            <q-icon name="info" size="14px" class="q-mr-xs" />
            Demo data
          </q-badge>
          <q-btn
            size="lg"
            flat
            dense
            round
            icon="refresh"
            class="refresh-btn"
            :loading="isLoading"
            @click="loadAll"
          >
            <q-tooltip>Refresh</q-tooltip>
          </q-btn>
        </div>
      </div>
    </section>

    <!-- STAT CARDS (only for whatever the user has rights to) -->
    <section class="stats-grid">
      <article
        class="stat-card"
        v-for="card in statCards"
        :key="card.label"
        :class="{ clickable: !!card.onClick }"
        @click="card.onClick && card.onClick()"
      >
        <div
          class="stat-icon"
          :style="{ background: card.bg, color: card.color }"
        >
          <q-icon :name="card.icon" size="22px" />
        </div>
        <div class="stat-body">
          <span class="stat-label">{{ card.label }}</span>
          <strong class="stat-value">{{ card.value }}</strong>
          <small v-if="card.hint" class="stat-hint">{{ card.hint }}</small>
        </div>
      </article>
    </section>

    <!-- SIMPLE SPLIT CHART: only shown when BOTH rights are present -->
    <section
      v-if="userHaveVisitRights && userHaveProjectRights"
      class="charts-grid single-col"
    >
      <article class="chart-card">
        <div class="chart-card-header">
          <h3>Visits vs Projects</h3>
          <span class="chart-total">{{ visitCount + projectCount }} total</span>
        </div>

        <div v-if="visitCount + projectCount" class="donut-legend">
          <div class="donut-wrap">
            <svg viewBox="0 0 42 42" class="donut-svg">
              <circle
                v-for="seg in splitDonutSegments"
                :key="seg.label"
                cx="21"
                cy="21"
                r="15.9"
                fill="transparent"
                :stroke="seg.color"
                stroke-width="6"
                :stroke-dasharray="seg.dash"
                :stroke-dashoffset="seg.offset"
              />
            </svg>
            <div class="donut-center">
              <strong>{{ visitCount + projectCount }}</strong>
              <span>Records</span>
            </div>
          </div>

          <ul class="legend-list">
            <li v-for="row in splitBreakdown" :key="row.label">
              <span
                class="legend-dot"
                :style="{ background: row.color }"
              ></span>
              <span class="legend-label">{{ row.label }}</span>
              <span class="legend-value">{{ row.count }}</span>
            </li>
          </ul>
        </div>
        <div v-else class="chart-empty">No data yet.</div>
      </article>
    </section>

    <!-- COMBINED RECENT ACTIVITY -->
    <section class="list-card recent-card">
      <div class="filter-header">
        <div class="section-title">
          <span class="section-icon">
            <q-icon :size="$q.screen.lt.md ? 'md' : ''" name="history" />
          </span>
          <div>
            <h2>Recent Activity</h2>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="loading-state">
        <q-spinner size="40px" color="primary" />
        <span>Loading activity...</span>
      </div>

      <div v-else-if="!allActivity.length" class="empty-state">
        <div class="empty-icon"><q-icon name="event_busy" size="38px" /></div>
        <h3>No activity found</h3>
        <p>No records to show yet.</p>
      </div>

      <template v-else>
        <div class="recent-list">
          <div
            class="recent-row"
            v-for="item in paginatedActivity"
            :key="item.type + '-' + item.id"
          >
            <div class="recent-main">
              <span
                class="type-icon"
                :class="item.type === 'visit' ? 'visit-icon' : 'project-icon'"
              >
                <q-icon
                  :name="item.type === 'visit' ? 'event_note' : 'work'"
                  size="16px"
                />
              </span>
              <div class="dealer-info">
                <strong>{{ item.title }}</strong>
                <small>{{ item.subtitle }}</small>
              </div>
            </div>
            <div class="recent-meta">
              <span
                class="purpose-badge"
                :class="item.type === 'visit' ? 'badge-visit' : 'badge-project'"
              >
                {{ item.type === "visit" ? "Visit" : "Project" }}
              </span>
              <span class="date-value">{{ formatDate(item.date) }}</span>
            </div>
          </div>
        </div>

        <!-- PAGINATION -->
        <div v-if="totalPages > 1" class="pagination-bar">
          <span class="pagination-info">
            Showing {{ pageStart }}–{{ pageEnd }} of {{ allActivity.length }}
          </span>
          <q-pagination
            v-model="page"
            :max="totalPages"
            :max-pages="$q.screen.lt.md ? 4 : 7"
            boundary-numbers
            direction-links
            color="primary"
            active-color="primary"
            :size="$q.screen.gt.md? 'sm' : 'md'"
          />
        </div>
      </template>
    </section>
  </section>
</template>

<script>
// Optional: wire these to your real service/API layer.
import apis from "@/utilities";
import api from "@/services/axios";

export default {
  name: "OverviewDashboard",

  props: {
    userHaveVisitRights: { type: Boolean, default: true },
    userHaveProjectRights: { type: Boolean, default: true },
    userHaveDashBoardRights: { type: Boolean, default: true },
  },

  data() {
    return {
      isLoading: false,
      usingDummyData: false,
      visits: [],
      projects: [],
      page: 1,
      perPage: 8,
      lastUpdated: null,
    };
  },

  computed: {
    url() {
      return this.$store?.getters?.url || "";
    },

    visitCount() {
      return this.visits.length;
    },

    projectCount() {
      return this.projects.length;
    },

    projectWonCount() {
      return this.projects.filter((p) => p.projectStage === "Won").length;
    },

    uniqueDealers() {
      const visitDealers = this.visits
        .map((v) => v.accountDealerName)
        .filter(Boolean);
      const projectDealers = this.projects
        .map((p) => p.dealerName)
        .filter(Boolean);
      return new Set([...visitDealers, ...projectDealers]).size;
    },

    // Only builds cards for the sections the user actually has rights to
    statCards() {
      const cards = [];
       if(this.userHaveDashBoardRights){
         if (this.userHaveVisitRights) {
           cards.push({
             label: "Total Visits",
             value: this.visitCount,
             icon: "event_note",
             bg: "#e7f2f4",
             color: "#146b80",
             onClick: () => this.$emit("go-to-tab", "visit"),
           });
         }
   
         if (this.userHaveProjectRights) {
           cards.push({
             label: "Total Projects",
             value: this.projectCount,
             icon: "work",
             bg: "#eef6ee",
             color: "#3f8a4a",
             onClick: () => this.$emit("go-to-tab", "project"),
           });
   
          //  cards.push({
          //    label: "Won Projects",
          //    value: this.projectWonCount,
          //    icon: "emoji_events",
          //    bg: "#f5edf8",
          //    color: "#7a4fa0",
          //  });
         }
   if (this.userHaveProjectRights || this.userHaveVisitRights) {
         cards.push({
           label: "Dealers Covered",
           value: this.uniqueDealers,
           icon: "store",
           bg: "#fdf3e8",
           color: "#c17a1f",
         });
   }
       }

      return cards;
    },

    splitBreakdown() {
      return [
        { label: "Visits", count: this.visitCount, color: "#146b80" },
        { label: "Projects", count: this.projectCount, color: "#e08e45" },
      ];
    },

    splitDonutSegments() {
      const total = this.visitCount + this.projectCount || 1;
      const circumference = 2 * Math.PI * 15.9;
      let offsetAcc = 0;

      return this.splitBreakdown.map((row) => {
        const fraction = row.count / total;
        const dashLength = fraction * circumference;
        const seg = {
          label: row.label,
          color: row.color,
          dash: `${dashLength} ${circumference - dashLength}`,
          offset: -offsetAcc,
        };
        offsetAcc += dashLength;
        return seg;
      });
    },

    allActivity() {
      const visitItems = this.userHaveVisitRights
        ? this.visits.map((v) => ({
            type: "visit",
            id: v.id,
            title: v.accountDealerName || "N/A",
            subtitle: [v.cityName, v.regionName].filter(Boolean).join(" · "),
            date: v.visitDate,
          }))
        : [];

      const projectItems = this.userHaveProjectRights
        ? this.projects.map((p) => ({
            type: "project",
            id: p.id,
            title: p.projectName || "N/A",
            subtitle: [p.dealerName, p.dealerCity].filter(Boolean).join(" · "),
            date: p.dateOfFirstVisit,
          }))
        : [];

      return this.userHaveDashBoardRights ? [...visitItems, ...projectItems].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      ) : [];
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.allActivity.length / this.perPage));
    },

    paginatedActivity() {
      const start = (this.page - 1) * this.perPage;
      return this.allActivity.slice(start, start + this.perPage);
    },

    pageStart() {
      return this.allActivity.length ? (this.page - 1) * this.perPage + 1 : 0;
    },

    pageEnd() {
      return Math.min(this.page * this.perPage, this.allActivity.length);
    },
  },

  mounted() {
    this.loadAll();
  },

  methods: {
    async loadAll() {
      this.isLoading = true;

      try {
        // Named promises — chahe koi ek right false ho, index confusion nahi hoga
        const visitPromise = this.userHaveVisitRights
          ? api.get(`${this.url}/${apis.GetList}`)
          : Promise.resolve(null);

        const projectPromise = this.userHaveProjectRights
          ? api.get(`${this.url}/${apis.GetProjectList}`)
          : Promise.resolve(null);

        const [visitRes, projectRes] = await Promise.all([
          visitPromise,
          projectPromise,
        ]);

        this.visits = this.userHaveVisitRights
          ? visitRes?.data?.data || []
          : [];
        this.projects = this.userHaveProjectRights
          ? projectRes?.data?.data || []
          : [];
        this.usingDummyData = false;
      } catch (err) {
        console.warn(
          "Dashboard API unavailable, using demo data:",
          err.message
        );
        this.$store?.dispatch("triggerUnauthorizedError", err);
        this.visits = this.userHaveVisitRights && [];
        this.projects = this.userHaveProjectRights && [];
        this.usingDummyData = true;
      } finally {
        this.lastUpdated = new Date();
        this.isLoading = false;
      }
    },

    formatDate(date) {
      if (!date) return "N/A";
      const value = String(date).substring(0, 10);
      const parts = value.split("-");
      if (parts.length !== 3) return value;
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    },
  },
  watch: {
    // Agar filters/data change ho to page ko reset kar dein taake invalid page par na atak jaye
    allActivity() {
      if (this.page > this.totalPages) {
        this.page = 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ower-view {
  display: flex;
  flex-direction: column;
}
.header-copy {
  font-size: 14px;
}
.pagination-bar {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #edf2f3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.pagination-info {
  color: #87969a;
  font-size: 12px;
}

@media (min-width: 641px) {
  .pagination-bar {
    flex-direction: row;
    justify-content: space-between;
  }
}

$primary: #146b80;
$primary-gradient: linear-gradient(135deg, #146b80, #3f9cae);

.overview-dashboard {
  text-align: left;
}

.header-actions-inline {
  display: flex;
  align-items: center;
  gap: 10px;
}

.demo-badge {
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

/* ================= TOP STRIP ================= */

.filter-card {
  padding: 20px 18px;
  background: #fff;
  border-bottom: 1px solid #e5edef;
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-icon {
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  color: $primary;
  background: #e7f2f4;
  border-radius: 10px;
}

.section-title h2 {
  margin: 0;
  color: $primary;
  font-size: 17px;
}

.refresh-btn {
  color: $primary;
}

/* ================= STAT CARDS ================= */

.stats-grid {
  padding: 18px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  background: #f5f8f9;
}

.stat-card {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff;
  border: 1px solid #e3ebed;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(27, 67, 77, 0.05);
}

.stat-card.clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.stat-card.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(27, 67, 77, 0.1);
}

.stat-icon {
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 10px;
}

.stat-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-label {
  color: #87969a;
  font-size: 12px;
}

.stat-value {
  color: $primary;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}

.stat-hint {
  color: #a4b0b3;
  font-size: 11px;
}

/* ================= CHARTS ================= */

.charts-grid {
  padding: 0 18px 18px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  background: #f5f8f9;
}

.chart-card {
  padding: 18px;
  background: #fff;
  border: 1px solid #e3ebed;
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(27, 67, 77, 0.05);
}

.chart-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.chart-card-header h3 {
  margin: 0;
  color: $primary;
  font-size: 15px;
}

.chart-total {
  color: #87969a;
  font-size: 11px;
}

.chart-empty {
  padding: 20px 0;
  color: #87969a;
  font-size: 13px;
  text-align: center;
}

/* --- donut --- */

.donut-legend {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
}

.donut-wrap {
  position: relative;
  width: 140px;
  height: 140px;
}

.donut-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.donut-center strong {
  color: $primary;
  font-size: 20px;
}

.donut-center span {
  color: #87969a;
  font-size: 11px;
}

.legend-list {
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-list li {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 9px;
  height: 9px;
  flex: 0 0 auto;
  border-radius: 50%;
}

.legend-label {
  flex: 1;
  min-width: 0;
  color: #526a70;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.legend-value {
  color: $primary;
  font-size: 12px;
  font-weight: 700;
}

/* ================= RECENT ACTIVITY ================= */

.list-card {
  background: #fff;
  overflow: hidden;
}

.recent-card {
  padding: 20px 18px;
}

.recent-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.recent-row {
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #f9fbfb;
  border: 1px solid #edf2f3;
  border-radius: 10px;
}

.recent-main {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.type-icon {
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 8px;
  color: #fff;
}

.type-icon.visit-icon {
  background: $primary-gradient;
}

.type-icon.project-icon {
  background: linear-gradient(135deg, #e08e45, #c17a1f);
}

.dealer-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.dealer-info strong {
  color: #35525a;
  font-size: 13px;
}

.dealer-info small {
  color: #87969a;
  font-size: 11px;
}

.recent-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.purpose-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 10px;
  white-space: nowrap;
}

.badge-visit {
  color: $primary;
  background: #e7f2f4;
}

.badge-project {
  color: #8a5a12;
  background: #fdf3e8;
}

.date-value {
  color: #87969a;
  font-size: 11px;
  white-space: nowrap;
}

/* ================= LOADING / EMPTY ================= */

.loading-state,
.empty-state {
  min-height: 200px;
  width: 100%;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.loading-state {
  gap: 12px;
  color: #73858a;
  font-size: 13px;
}

.empty-icon {
  width: 64px;
  height: 64px;
  margin-bottom: 14px;
  display: grid;
  place-items: center;
  color: $primary;
  background: #e7f2f4;
  border-radius: 50%;
}

.empty-state h3 {
  margin: 0;
  color: $primary;
  font-size: 16px;
}

.empty-state p {
  margin: 6px 0 0;
  color: #819297;
  font-size: 13px;
}

/* ============================================================
   TABLET / DESKTOP
   ============================================================ */

@media (min-width: 641px) {
  .filter-card {
    padding: 25px 34px;
  }

  .stats-grid,
  .charts-grid {
    padding-left: 34px;
    padding-right: 34px;
  }

  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .recent-card {
    padding: 24px 34px;
  }
}

@media (min-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .donut-legend {
    flex-direction: row;
    align-items: center;
    gap: 24px;
  }
}
</style>